import { Fragment, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Link } from '~/components/global';
import { ProgressBar, TacoRating, Button } from '@appsumo/dorado-react';

import { DISCUSSION_REVIEWS, DiscussionType } from '~/lib/discussions/types';
import { Deal, DealReview } from '~/types/deal';
import { DealImage } from '../product';

function CommentList({
  title,
  prefix,
  comments,
}: {
  title: string;
  prefix: string;
  comments: any[];
}) {
  const router = useRouter();
  const params = router.query as { id: string; slug: string };
  const currentDiscussionId = parseInt(
    params.id.split('-').pop() as string,
    10,
  );

  const nonCurrentComments = useMemo(
    () => comments.filter((comment) => comment.id !== currentDiscussionId),
    [comments, currentDiscussionId],
  );

  if (nonCurrentComments.length === 0) {
    return null;
  }

  return (
    <div className="my-4">
      <h5 className="font-header text-base font-bold">{title}</h5>
      <ul>
        {nonCurrentComments.map((comment) => (
          <li key={comment.id}>
            <Link
              href={`${prefix}${comment.url_path_suffix}`}
              className="my-4 text-blue-600 line-clamp-2 hover:underline"
            >
              {comment.title || comment.comment}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DealRatings({ deal }: { deal: Deal }) {
  return (
    <div className="my-6">
      <AverageRating
        average={deal.deal_review.average_rating}
        count={deal.deal_review.review_count}
      />

      <BreakoutRating dealReview={deal.deal_review} />
    </div>
  );
}

function AverageRating({ average, count }: { average: number; count: number }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded border border-gravel p-3">
      {!!average && (
        <h3 className="font-header text-4xl font-bold">
          {new Intl.NumberFormat().format(average)}
        </h3>
      )}
      <TacoRating averageRating={average} height={16} href="" />
      <p>{count} ratings</p>
    </div>
  );
}

function BreakoutRating({ dealReview }: { dealReview: DealReview }) {
  const totalReviews = dealReview.review_count;

  const individualRatingCounts: number[] = [
    dealReview.review_count_5_tacos || 0,
    dealReview.review_count_4_tacos || 0,
    dealReview.review_count_3_tacos || 0,
    dealReview.review_count_2_tacos || 0,
    dealReview.review_count_1_tacos || 0,
  ];

  return (
    <div className="mt-6 rounded border border-gravel p-4">
      <h4 className="text-xl font-semibold">Taco ratings</h4>

      <div className="mt-4 grid grid-cols-breakout-ratings items-center gap-y-5 gap-x-4">
        {individualRatingCounts.map((rating, index) => {
          return (
            <Fragment key={`taco-rating-${index}`}>
              <span>{5 - index} tacos</span>
              <ProgressBar
                max={100}
                value={(rating / totalReviews) * 100}
                variant="primary"
                className="h-4 rounded"
              />
              <strong>{Intl.NumberFormat().format(rating)}</strong>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function ProductDetails({
  deal,
  discussionSlug,
}: {
  deal: any;
  discussionSlug: DiscussionType;
}) {
  const subHeader = deal.products[0]?.story?.subheader || '';

  return (
    <div className="mt-4 md:mt-0">
      <div className="hidden md:block">
        <Link href={`/products/${deal.slug}/`} className="max-sm:hidden">
          <DealImage
            deal={deal}
            priority={true}
            width={400}
            height={225}
            className="rounded"
          />
        </Link>
        <Link href={`/products/${deal.slug}/`} className="max-sm:hidden">
          <h2 className="my-4 font-header text-2xl font-bold">
            {deal.public_name}
          </h2>
        </Link>
        {deal.card_description && (
          <Link href={`/products/${deal.slug}/`} className="max-sm:hidden">
            <p className="mb-4 line-clamp-2">{deal.card_description}</p>
          </Link>
        )}
        {subHeader && !deal.is_info && <p className="mb-4">{subHeader}</p>}
        <Button href={`/products/${deal.slug}/`} className="inline-block">
          Product details
        </Button>
        {discussionSlug === DISCUSSION_REVIEWS && <DealRatings deal={deal} />}
      </div>
      <CommentList
        title="Latest questions"
        prefix={`/products/${deal.slug}/`}
        comments={deal.top_5_questions}
      />
      <hr />
      <CommentList
        title="Latest reviews"
        prefix={`/products/${deal.slug}/`}
        comments={deal.top_5_reviews}
      />
      <hr />
      <Link
        href={`/products/${deal.slug}/#questions`}
        className="my-4 text-bolt line-clamp-2 hover:underline"
      >
        See all questions {deal.is_info ? '' : ' and reviews '} for this product
      </Link>

      <Button href={`/products/${deal.slug}/#${discussionSlug}`}>
        {discussionSlug === DISCUSSION_REVIEWS
          ? 'Leave a review'
          : 'Ask a question'}
      </Button>
    </div>
  );
}
